import React, { useState, useEffect, useRef } from 'react';
import { Box } from 'lucide-react';
import { getBrandColor } from '../utils/tools/logos';

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  fallbackSrc?: string;
  toolId?: string;
  className?: string;
  width?: number;
  height?: number;
  itemProp?: string;
}

function extractDomain(url: string): string | null {
  try {
    if (url.includes('logo.clearbit.com/')) {
      return url.split('logo.clearbit.com/')[1];
    }
    return null;
  } catch {
    return null;
  }
}

export function ImageWithFallback({
  src,
  alt,
  fallbackSrc,
  toolId,
  className = '',
  width,
  height,
  itemProp
}: ImageWithFallbackProps) {
  const [imgSrc, setImgSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [failCount, setFailCount] = useState(0);
  const [showIconFallback, setShowIconFallback] = useState(false);
  const originalSrc = useRef(src);
  const brandColor = toolId ? getBrandColor(toolId) : '3B82F6';

  useEffect(() => {
    originalSrc.current = src;
    setImgSrc(src);
    setFailCount(0);
    setIsLoading(true);
    setShowIconFallback(false);
  }, [src]);

  const handleError = () => {
    const domain = extractDomain(originalSrc.current);

    if (failCount === 0 && domain) {
      setImgSrc(`https://www.google.com/s2/favicons?domain=${domain}&sz=128`);
      setFailCount(1);
    } else if (failCount === 1 && domain) {
      setImgSrc(`https://icon.horse/icon/${domain}`);
      setFailCount(2);
    } else {
      setShowIconFallback(true);
      setIsLoading(false);
    }
  };

  if (showIconFallback) {
    return (
      <div
        className={`flex items-center justify-center rounded-lg ${className}`}
        style={{
          width: width ? `${width}px` : '100%',
          height: height ? `${height}px` : '100%',
          backgroundColor: `#${brandColor}15`
        }}
      >
        <Box
          className="w-1/2 h-1/2"
          style={{ color: `#${brandColor}` }}
        />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ width: width ? `${width}px` : '100%', height: height ? `${height}px` : '100%' }}>
      {isLoading && (
        <div
          className="absolute inset-0 bg-gray-100 rounded-lg animate-pulse"
          style={{
            backgroundImage: 'linear-gradient(90deg, #f3f4f6 0px, #e5e7eb 200px, #f3f4f6 400px)',
            backgroundSize: '600px 100%',
            animation: 'shimmer 1.5s infinite linear'
          }}
          aria-hidden="true"
        />
      )}

      <img
        loading="lazy"
        decoding="async"
        src={imgSrc}
        alt={alt}
        width={width}
        height={height}
        itemProp={itemProp}
        onError={handleError}
        onLoad={() => setIsLoading(false)}
        className={`w-full h-full object-contain transition-opacity duration-300 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ aspectRatio: width && height ? `${width} / ${height}` : 'auto' }}
      />
    </div>
  );
}